import { Drawer } from '@base-ui/react/drawer';
import { CommandPalette } from '@/components/command-palette/CommandPalette';
import { AddUrlModal } from '@/components/header/AddUrlModal';
import { JobDetail } from '@/components/job-detail/JobDetail';
import { JobFeed } from '@/components/job-feed/JobFeed';
import { Settings } from '@/components/Settings';
import { Shell } from '@/components/Shell';
import { Tracker } from '@/components/tracker/Tracker';
import { cx } from '@/cva.config';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import {
  useHasSelection,
  useSelectJob,
} from '@/providers/dashboard/JobBrowserContext';
import {
  useOverlays,
  useSettingsNav,
  useShellNav,
} from '@/providers/dashboard/ShellContext';
import { SettingsProvider } from '@/providers/settings/SettingsProvider';

interface DashboardProps {
  onReplayOnboarding: () => void;
}

export const Dashboard = ({ onReplayOnboarding }: DashboardProps) => {
  const { view, setView } = useShellNav();
  const { addUrlOpen } = useOverlays();
  const { setSettingsDirty, pendingView, resolvePendingView } =
    useSettingsNav();
  const hasSelection = useHasSelection();
  const select = useSelectJob();

  useKeyboardShortcuts();

  const isFeed = view === 'feed';
  const isTracker = view === 'tracker';
  const isSettings = view === 'settings';

  return (
    <Shell>
      {isFeed && (
        <div className="relative flex min-h-0 flex-1 overflow-hidden">
          <div className="flex min-w-0 flex-1 flex-col">
            <JobFeed />
          </div>
        </div>
      )}
      {isTracker && <Tracker />}
      {isSettings && (
        <SettingsProvider
          onClose={() => setView('feed')}
          onDirtyChange={setSettingsDirty}
          onPendingNavigationResolved={resolvePendingView}
          onReplayOnboarding={onReplayOnboarding}
          pendingNavigation={pendingView}
        >
          <Settings />
        </SettingsProvider>
      )}
      <Drawer.Root
        onOpenChange={(open) => {
          if (!open) select(null);
        }}
        open={hasSelection}
        swipeDirection="right"
      >
        <Drawer.Portal>
          <Drawer.Backdrop
            className={cx(
              'fixed inset-x-0 top-header bottom-status-bar z-40 bg-black/30 transition-opacity duration-200 data-ending-style:opacity-0 data-starting-style:opacity-0',
            )}
          />
          <Drawer.Viewport
            className={cx(
              'fixed inset-x-0 top-header bottom-status-bar z-40 flex justify-end',
            )}
          >
            <Drawer.Popup
              className={cx(
                'h-full w-full max-w-190 border-l border-border bg-surface shadow-lg transition-transform duration-200 ease-out data-ending-style:translate-x-full data-starting-style:translate-x-full xl:max-w-205',
              )}
            >
              {hasSelection && <JobDetail />}
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.Root>
      {addUrlOpen && <AddUrlModal />}
      <CommandPalette />
    </Shell>
  );
};
